import React, {useState} from 'react';
import {Box, Button, CircularProgress} from '@mui/material';
import {useMutation} from '@apollo/client';
import {CREATE_CONNECT_SESSION} from '../../graphql/mutations';
import {
  GET_MY_CONNECTIONS,
  GET_ORG_CONNECTIONS,
} from '../../graphql/queries';
import {ConnectionTier} from '../../types/generated/graphql';
import {IntegrationLogo, getBrandColor} from '../../integrations';

interface Props {
  integrationId: string;
  tier: ConnectionTier;
  orgId?: string;
  runId?: string;
  label?: string;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

export const ConnectButton: React.FC<Props> = ({
  integrationId,
  tier,
  orgId,
  runId,
  label = 'Connect Integration',
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
}) => {
  const [loading, setLoading] = useState(false);

  const refetchQueries =
    tier === ConnectionTier.Org && orgId
      ? [{query: GET_ORG_CONNECTIONS, variables: {orgId}}]
      : [{query: GET_MY_CONNECTIONS}];

  const [createSession] = useMutation(CREATE_CONNECT_SESSION, {
    refetchQueries,
  });

  const handleConnect = async () => {
    setLoading(true);
    try {
      const result = await createSession({
        variables: {
          integrationId,
          tier,
          orgId: orgId ?? null,
          runId: runId ?? null,
        },
      });
      const url = result.data?.createConnectSession?.connectUrl;
      if (url) {
        window.open(url, '_blank', 'width=600,height=700');
      }
    } catch (err) {
      console.error('Failed to create connect session:', err);
    } finally {
      setLoading(false);
    }
  };

  // Brand-color the contained variant so each integration's connect
  // CTA reads as that brand. Fall back to the default theme primary
  // when the manifest entry has no branding registered.
  const brandColor = getBrandColor(integrationId);
  const brandSx =
    variant === 'contained' && brandColor
      ? {
          bgcolor: brandColor,
          '&:hover': {bgcolor: brandColor, filter: 'brightness(0.92)'},
        }
      : undefined;

  return (
    <Button
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      startIcon={
        loading ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              // For contained brand-colored buttons we render the
              // logo monochrome (white) so it pops against the brand
              // background; outlined / text buttons get the full-color
              // mark since their background is neutral.
              color: variant === 'contained' ? '#fff' : undefined,
            }}
          >
            <IntegrationLogo
              integrationId={integrationId}
              name={label}
              size={16}
              variant={variant === 'contained' ? 'mono' : 'color'}
            />
          </Box>
        )
      }
      onClick={handleConnect}
      disabled={loading}
      sx={brandSx}
    >
      {label}
    </Button>
  );
};

